import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Brand from "@/components/Brand";
import UserSearchModal from "@/components/UserSearchModal";

const TIERS = ["free", "pro", "elite"];
const ROLLOUT_LEVELS = ["off", "owner", "admins", "elite", "pro", "all"];

export default function AdminPage() {
  const { user, logout } = useAuth();
  const [rows, setRows] = useState([]);
  const [billingByUser, setBillingByUser] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [flags, setFlags] = useState([]);
  const [flagForm, setFlagForm] = useState({ name: "", rollout: "owner", description: "" });
  const [isOwner, setIsOwner] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, mf] = await Promise.all([
        api.get("/admin/users", { params: { limit: 500 } }),
        api.get("/admin/flags/me"),
      ]);
      setRows(u.data.users || []);
      setIsOwner(mf.data?.is_owner || false);
    } catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  const loadFlags = async () => {
    try { const { data } = await api.get("/admin/flags"); setFlags(data); }
    catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); loadFlags(); }, []);

  async function apply(u, patch) {
    // V2.5.2: role flips require the secure operator password again.
    if (patch.role && patch.role !== u.role) {
      const pwd = window.prompt("Operator password to change role:");
      if (!pwd) return;
      const reason = window.prompt(`Reason for ${patch.role === "admin" ? "promoting" : "demoting"} ${u.email}:`);
      if (!reason) return;
      patch = { ...patch, admin_password: pwd, reason };
    }
    if (patch.status && ["banned", "restricted"].includes(patch.status)) {
      const reason = window.prompt(`Reason for setting status to ${patch.status}:`);
      if (!reason) return;
      patch = { ...patch, reason };
    }
    try { await api.patch(`/admin/users/${u.id}`, patch); await load(); }
    catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
  }
  async function setUserTier(u, tier) {
    try {
      await api.post(`/admin/users/${u.id}/tier`, { tier, lock: true });
      setBillingByUser(prev => ({ ...prev, [u.id]: tier }));
    } catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
  }
  async function resetUser(u) { try { await api.post(`/admin/users/${u.id}/reset`); await load(); } catch (e) { setErr(fmtErr(e.response?.data?.detail)); } }
  async function deleteUser(u) { try { await api.delete(`/admin/users/${u.id}`); await load(); } catch (e) { setErr(fmtErr(e.response?.data?.detail)); } setConfirm(null); }

  async function saveFlag(e) {
    e.preventDefault();
    try {
      await api.post("/admin/flags", flagForm);
      setFlagForm({ name: "", rollout: "owner", description: "" });
      await loadFlags();
    } catch (e2) { setErr(fmtErr(e2.response?.data?.detail) || e2.message); }
  }
  async function deleteFlag(name) {
    if (!window.confirm(`Delete flag "${name}"?`)) return;
    try { await api.delete(`/admin/flags/${name}`); await loadFlags(); }
    catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
  }

  return (
    <div className="min-h-screen bg-[#0b0e14] text-slate-200 font-mono" data-testid="admin-page">
      <header className="bg-[#11151d] border-b border-slate-800 px-6 py-3 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-2">
          <Brand compact />
          <span className="text-amber-400 font-bold text-[10px] uppercase ml-1">· Admin</span>
        </Link>
        <div className="flex items-center gap-3 text-xs">
          <Link to="/app" className="text-slate-400 hover:text-white">Sandbox</Link>
          <Link to="/account" className="text-slate-400 hover:text-white">Account</Link>
          <span className="text-amber-400 font-bold">Admin</span>
          <button onClick={logout} className="text-slate-500 hover:text-rose-400"><i className="fa-solid fa-right-from-bracket"/></button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-white">User Management</h1>
            <p className="text-xs text-slate-500">{rows.length} total users</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} data-testid="admin-refresh" className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-md text-xs font-bold">
              <i className="fa-solid fa-arrows-rotate mr-1"/> Refresh
            </button>
            <button onClick={() => setShowSearch(true)} data-testid="admin-search-btn" className="px-3 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 text-blue-300 rounded-md text-xs font-bold">
              <i className="fa-solid fa-magnifying-glass mr-1"/> Search
            </button>
          </div>
        </div>
        {showSearch && <UserSearchModal onClose={() => setShowSearch(false)} />}
        {err && <div data-testid="admin-err" className="mb-3 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/20 px-3 py-2 rounded">{err}</div>}

        <div className="bg-[#11151d] border border-slate-800 rounded-2xl overflow-hidden">
          <table className="w-full text-xs" data-testid="admin-users-table">
            <thead className="bg-slate-900/60 text-slate-500 uppercase text-[10px]">
              <tr>
                <th className="text-left px-3 py-2.5">Email</th>
                <th className="text-left px-3 py-2.5">Name</th>
                <th className="text-left px-3 py-2.5">Role</th>
                <th className="text-left px-3 py-2.5">Status</th>
                <th className="text-right px-3 py-2.5">Balance</th>
                <th className="text-right px-3 py-2.5">Trades</th>
                <th className="text-right px-3 py-2.5">Lifetime PnL</th>
                <th className="text-right px-3 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center py-6 text-slate-500"><i className="fa-solid fa-spinner fa-spin"/></td></tr>}
              {rows.map(u => {
                const isMe = u.id === user?.id;
                return (
                  <tr key={u.id} data-testid={`row-${u.email}`} className={`border-t border-slate-800/60 ${isMe ? "bg-blue-500/5" : ""}`}>
                    <td className="px-3 py-2 text-slate-200">{u.email}{isMe && <span className="text-[9px] text-blue-400 ml-1">(you)</span>}</td>
                    <td className="px-3 py-2 text-slate-400">{u.name}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${u.role === "admin" ? "bg-amber-500/20 text-amber-300" : "bg-slate-700/40 text-slate-300"}`}>{u.role}</span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${u.status === "active" ? "bg-emerald-500/20 text-emerald-300" : u.status === "restricted" ? "bg-amber-500/20 text-amber-300" : "bg-rose-500/20 text-rose-300"}`}>{u.status || "active"}</span>
                    </td>
                    <td className="px-3 py-2 text-right text-emerald-300">${(u.balance || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-slate-400">{u.trades}</td>
                    <td className={`px-3 py-2 text-right font-bold ${u.lifetime_pnl >= 0 ? "text-emerald-400" : "text-rose-400"}`}>${u.lifetime_pnl.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right">
                      {isMe ? <span className="text-slate-600 text-[10px]">protected</span> : (
                        <div className="flex justify-end gap-1.5 flex-wrap">
                          {u.status !== "banned" && <Btn testid={`ban-${u.email}`} color="rose" onClick={() => apply(u, { status: "banned" })}>Ban</Btn>}
                          {u.status !== "restricted" && <Btn testid={`restrict-${u.email}`} color="amber" onClick={() => apply(u, { status: "restricted" })}>Restrict</Btn>}
                          {u.status !== "active" && <Btn testid={`activate-${u.email}`} color="emerald" onClick={() => apply(u, { status: "active" })}>Activate</Btn>}
                          <Btn testid={`role-${u.email}`} color="purple" onClick={() => apply(u, { role: u.role === "admin" ? "user" : "admin" })}>{u.role === "admin" ? "Demote" : "Promote"}</Btn>
                          <select data-testid={`tier-${u.email}`} value={billingByUser[u.id] || ""}
                            onChange={e => setUserTier(u, e.target.value)}
                            className="text-[10px] bg-slate-800 text-amber-300 border border-amber-500/30 rounded px-1 py-0.5 font-bold uppercase cursor-pointer">
                            <option value="" disabled>Set Tier</option>
                            {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                          <Btn testid={`reset-${u.email}`} color="blue" onClick={() => resetUser(u)}>Reset</Btn>
                          <Btn testid={`delete-${u.email}`} color="rose" outline onClick={() => setConfirm({ action: "delete", user: u })}>Delete</Btn>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Feature Flags (master-owner only) */}
        <div className="mt-8 bg-[#11151d] border border-slate-800 rounded-2xl p-5" data-testid="flag-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-lg font-bold text-white"><i className="fa-solid fa-flag mr-2 text-amber-400"/> Feature Flags</h2>
              <p className="text-[10px] text-slate-500">Owner can stage new features behind a rollout level. Read-only for non-owner admins.</p>
            </div>
            {!isOwner && <span className="text-[10px] text-slate-500 uppercase font-bold">Read-only</span>}
          </div>

          {isOwner && (
            <form onSubmit={saveFlag} className="grid grid-cols-[1fr_140px_2fr_auto] gap-2 mb-4" data-testid="flag-form">
              <input data-testid="flag-name" required placeholder="flag_name (snake_case)" value={flagForm.name}
                onChange={e => setFlagForm({ ...flagForm, name: e.target.value.replace(/\s+/g, "_") })}
                className="px-3 py-1.5 bg-[#161d28] border border-slate-700 rounded text-xs"/>
              <select data-testid="flag-rollout" value={flagForm.rollout} onChange={e => setFlagForm({ ...flagForm, rollout: e.target.value })}
                className="px-2 py-1.5 bg-[#161d28] border border-slate-700 rounded text-xs">
                {ROLLOUT_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <input data-testid="flag-desc" placeholder="description" value={flagForm.description}
                onChange={e => setFlagForm({ ...flagForm, description: e.target.value })}
                className="px-3 py-1.5 bg-[#161d28] border border-slate-700 rounded text-xs"/>
              <button data-testid="flag-save" type="submit" className="px-3 py-1.5 bg-amber-500 text-slate-900 rounded text-xs font-bold">Save</button>
            </form>
          )}

          <table className="w-full text-xs">
            <thead className="text-slate-500 uppercase text-[10px]">
              <tr>
                <th className="text-left px-2 py-1">Name</th>
                <th className="text-left px-2 py-1">Rollout</th>
                <th className="text-left px-2 py-1">Description</th>
                <th className="text-right px-2 py-1">Updated By</th>
                {isOwner && <th className="text-right px-2 py-1">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {flags.length === 0 && <tr><td colSpan={5} className="text-center py-4 text-slate-600">No flags configured.</td></tr>}
              {flags.map(f => (
                <tr key={f.name} data-testid={`flag-row-${f.name}`} className="border-t border-slate-800/60">
                  <td className="px-2 py-2 font-mono text-cyan-300">{f.name}</td>
                  <td className="px-2 py-2">
                    {isOwner ? (
                      <select data-testid={`flag-set-${f.name}`} value={f.rollout}
                        onChange={async e => { await api.post("/admin/flags", { name: f.name, rollout: e.target.value, description: f.description || "" }); await loadFlags(); }}
                        className="bg-slate-800 text-amber-300 border border-amber-500/30 rounded px-1 py-0.5 font-bold uppercase text-[10px] cursor-pointer">
                        {ROLLOUT_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    ) : (
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-slate-700/40 text-amber-300">{f.rollout}</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-slate-400">{f.description || "—"}</td>
                  <td className="px-2 py-2 text-right text-slate-500">{f.updated_by || "—"}</td>
                  {isOwner && (
                    <td className="px-2 py-2 text-right">
                      <button data-testid={`flag-del-${f.name}`} onClick={() => deleteFlag(f.name)} className="text-rose-400 hover:text-rose-300 text-[10px]"><i className="fa-solid fa-trash"/></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <SocialManagementPanel />
      </div>

      {confirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#151a24] border border-slate-800 rounded-2xl max-w-md w-full p-5">
            <div className="font-bold text-white mb-2">Delete <span className="text-rose-300">{confirm.user.email}</span>?</div>
            <div className="text-xs text-slate-400 mb-4">This permanently removes the user, all trades, quizzes, and billing data.</div>
            <div className="flex gap-2">
              <button data-testid="confirm-delete" onClick={() => deleteUser(confirm.user)} className="flex-1 py-2 bg-rose-600 hover:bg-rose-700 rounded-lg text-xs font-bold">Delete</button>
              <button onClick={() => setConfirm(null)} className="flex-1 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-xs font-bold">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SocialManagementPanel() {
  const [state, setState] = useState({ global_frozen: false });
  const [logs, setLogs] = useState([]);
  const [muteForm, setMuteForm] = useState({ user_id: "", channel: "global", duration_hours: 24, reason: "" });
  const [broadcastForm, setBroadcastForm] = useState({ to: "all", body: "", subject: "" });
  const [reports, setReports] = useState([]);
  const [guilds, setGuilds] = useState([]);
  const [audit, setAudit] = useState([]);
  const [showFiles, setShowFiles] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => { reload(); }, []);
  async function reload() {
    try {
      const [s, l, r, g, a] = await Promise.all([
        api.get("/moderation/state"),
        api.get("/moderation/logs", { params: { limit: 25 } }),
        api.get("/social/reports").catch(() => ({ data: [] })),
        api.get("/guilds"),
        api.get("/admin/audit", { params: { limit: 100 } }).catch(() => ({ data: { entries: [] } })),
      ]);
      setState(s.data); setLogs(l.data); setReports(r.data); setGuilds(g.data);
      setAudit(a.data?.entries || []);
    } catch {}
  }
  async function toggleFreeze() {
    try { await api.post("/moderation/freeze", { frozen: !state.global_frozen }); await reload(); }
    catch {}
  }
  async function mute(e) {
    e.preventDefault();
    try { await api.post("/moderation/mute", muteForm); setMsg("User muted"); setTimeout(() => setMsg(""), 1500); await reload(); }
    catch {}
  }
  async function broadcast(e) {
    e.preventDefault();
    if (!window.confirm(`Broadcast this message to ${broadcastForm.to === "all" ? "ALL users" : "user " + broadcastForm.to}?`)) return;
    try {
      const { data } = await api.post("/moderation/broadcast", broadcastForm);
      setMsg(`Delivered to ${data.delivered} user(s)`);
      setBroadcastForm({ to: "all", body: "", subject: "" });
      setTimeout(() => setMsg(""), 2500);
    } catch {}
  }
  async function dissolveGuild(id) {
    if (!window.confirm("Dissolve this guild for ToS violation?")) return;
    try { await api.post(`/guilds/${id}/dissolve`); await reload(); } catch {}
  }

  return (
    <div className="bg-[#11151d] border border-slate-800 rounded-2xl p-4" data-testid="social-management">
      <h2 className="text-base font-bold text-white mb-3"><i className="fa-solid fa-shield-halved text-rose-300 mr-2"/>Social Management</h2>
      {msg && <div className="text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-1 mb-2">{msg}</div>}

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-3">
            <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Global Chat</div>
            <div className="flex items-center justify-between">
              <div className={`text-xs font-bold ${state.global_frozen ? "text-rose-300" : "text-emerald-300"}`}>
                {state.global_frozen ? "FROZEN" : "ACTIVE"}
              </div>
              <button data-testid="freeze-toggle" onClick={toggleFreeze}
                className={`px-3 py-1 text-[10px] font-bold rounded ${state.global_frozen ? "bg-emerald-600 text-white" : "bg-rose-600 text-white"}`}>
                {state.global_frozen ? "Unfreeze" : "Freeze"}
              </button>
            </div>
          </div>

          <form onSubmit={mute} className="bg-slate-900/40 border border-slate-800 rounded-lg p-3 space-y-2" data-testid="mute-form">
            <div className="text-[10px] uppercase font-bold text-slate-500">Mute User</div>
            <input required placeholder="User ID" value={muteForm.user_id} onChange={e => setMuteForm({...muteForm, user_id: e.target.value})}
              className="w-full px-2 py-1 bg-[#161d28] border border-slate-700 rounded text-xs"/>
            <div className="grid grid-cols-3 gap-2">
              <select value={muteForm.channel} onChange={e => setMuteForm({...muteForm, channel: e.target.value})}
                className="px-2 py-1 bg-[#161d28] border border-slate-700 rounded text-xs">
                <option value="global">Global</option><option value="guild">Guild</option><option value="dm">DM</option>
              </select>
              <input type="number" min={1} max={8760} value={muteForm.duration_hours}
                onChange={e => setMuteForm({...muteForm, duration_hours: parseInt(e.target.value) || 24})}
                className="px-2 py-1 bg-[#161d28] border border-slate-700 rounded text-xs"/>
              <button type="submit" className="px-2 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded text-xs font-bold">Mute</button>
            </div>
          </form>

          <form onSubmit={broadcast} className="bg-slate-900/40 border border-slate-800 rounded-lg p-3 space-y-2" data-testid="broadcast-form">
            <div className="text-[10px] uppercase font-bold text-slate-500">Server Broadcast</div>
            <div className="flex gap-2">
              <input placeholder="To (user_id or 'all')" value={broadcastForm.to} onChange={e => setBroadcastForm({...broadcastForm, to: e.target.value})}
                className="flex-1 px-2 py-1 bg-[#161d28] border border-slate-700 rounded text-xs"/>
              <input placeholder="Subject" value={broadcastForm.subject} onChange={e => setBroadcastForm({...broadcastForm, subject: e.target.value})}
                className="w-32 px-2 py-1 bg-[#161d28] border border-slate-700 rounded text-xs"/>
            </div>
            <textarea required rows={2} placeholder="Message body" value={broadcastForm.body} onChange={e => setBroadcastForm({...broadcastForm, body: e.target.value})}
              className="w-full px-2 py-1 bg-[#161d28] border border-slate-700 rounded text-xs resize-none"/>
            <button type="submit" className="w-full py-1.5 bg-amber-500 hover:bg-amber-400 text-slate-900 rounded text-xs font-bold">Broadcast</button>
          </form>
        </div>

        <div className="space-y-3">
          <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-3">
            <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Guilds ({guilds.length})</div>
            <div className="max-h-32 overflow-y-auto space-y-1">
              {guilds.map(g => (
                <div key={g.id} className="flex items-center justify-between text-xs">
                  <span className="truncate">{g.name} <span className="text-slate-500">· {g.status}</span></span>
                  <button onClick={() => dissolveGuild(g.id)} data-testid={`dissolve-${g.id}`} className="text-[10px] text-rose-400 hover:text-rose-300 ml-2">Dissolve</button>
                </div>
              ))}
              {guilds.length === 0 && <div className="text-xs text-slate-600">None</div>}
            </div>
          </div>
          <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-3">
            <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Reports inbox ({reports.length})</div>
            <div className="max-h-32 overflow-y-auto space-y-1 text-xs">
              {reports.slice(0, 10).map(r => (
                <div key={r.id} className="bg-slate-900/40 rounded px-2 py-1">
                  <div className="text-rose-300">{r.reason}</div>
                  <div className="text-slate-500 text-[10px]">{r.reporter_name} → {r.target_name}</div>
                </div>
              ))}
              {reports.length === 0 && <div className="text-xs text-slate-600">No reports</div>}
            </div>
          </div>
          <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-3">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase font-bold text-slate-500">Moderation log</div>
              <button data-testid="open-the-files" onClick={() => setShowFiles(true)}
                className="text-[10px] font-bold text-amber-300 hover:text-amber-200">
                <i className="fa-solid fa-folder-open mr-1"/> The Files
              </button>
            </div>
            <div className="max-h-32 overflow-y-auto space-y-0.5 text-[10px] text-slate-400">
              {logs.map(l => (
                <div key={l.id || l.at}>{(l.at || "").slice(11,19)} · {l.action || "filtered"} · {l.matched_term || l.message_id || l.channel || "—"}</div>
              ))}
              {logs.length === 0 && <div className="text-slate-600">empty</div>}
            </div>
          </div>
          <StrikesPanel />
        </div>
      </div>

      {showFiles && (
        <div data-testid="the-files-modal" className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4" onClick={() => setShowFiles(false)}>
          <div className="bg-[#0f131b] border border-amber-500/30 rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-white"><i className="fa-solid fa-folder-open text-amber-300 mr-2"/>The Files</h3>
                <p className="text-[10px] text-slate-500">Permanent record of every ban, restriction, and role change — including required reason.</p>
              </div>
              <button onClick={() => setShowFiles(false)} className="text-slate-500 hover:text-rose-300"><i className="fa-solid fa-xmark"/></button>
            </div>
            <div className="overflow-y-auto p-3 space-y-1.5 flex-1">
              {audit.length === 0 && <div className="text-xs text-slate-500 text-center py-8">No archived actions yet.</div>}
              {audit.filter(a => a.change?.reason || a.change?.status || a.change?.role).map((row, i) => (
                <div key={i} className="bg-slate-900/40 border border-slate-800 rounded-lg p-2.5">
                  <div className="flex items-baseline justify-between text-[10px] text-slate-500">
                    <span>{(row.at || "").slice(0,19).replace("T"," ")}</span>
                    <span className="text-amber-300">{row.actor_email}</span>
                  </div>
                  <div className="text-xs text-white mt-1">
                    Target <span className="text-slate-400">{row.target_id?.slice(0,8)}…</span> →{" "}
                    {row.change?.status && <span className="text-rose-300">{row.change.status.from} ▸ <b>{row.change.status.to}</b> </span>}
                    {row.change?.role && <span className="text-purple-300">role {row.change.role.from} ▸ <b>{row.change.role.to}</b> </span>}
                    {row.change?.action && <span className="text-blue-300">[{row.change.action}]</span>}
                  </div>
                  {row.change?.reason && <div className="text-[11px] text-amber-200 mt-1 italic">"{row.change.reason}"</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Btn({ children, color, outline, onClick, testid }) {
  const map = {
    rose:    outline ? "border-rose-500/40 text-rose-300 hover:bg-rose-500/10"       : "bg-rose-600/80 hover:bg-rose-600 text-white",
    amber:   outline ? "border-amber-500/40 text-amber-300 hover:bg-amber-500/10"    : "bg-amber-600/80 hover:bg-amber-600 text-white",
    emerald: outline ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" : "bg-emerald-600/80 hover:bg-emerald-600 text-white",
    blue:    outline ? "border-blue-500/40 text-blue-300 hover:bg-blue-500/10"       : "bg-blue-600/80 hover:bg-blue-600 text-white",
    purple:  outline ? "border-purple-500/40 text-purple-300 hover:bg-purple-500/10" : "bg-purple-600/80 hover:bg-purple-600 text-white",
  };
  return (
    <button data-testid={testid} onClick={onClick}
      className={`text-[10px] font-bold px-2 py-1 rounded ${outline ? "border" : ""} ${map[color]}`}>
      {children}
    </button>
  );
}


function StrikesPanel() {
  const [strikes, setStrikes] = useState([]);
  const [form, setForm] = useState({ username: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  async function reload() {
    try { const { data } = await api.get("/strikes"); setStrikes(data); }
    catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
  }
  useEffect(() => { reload(); }, []);

  async function issue(e) {
    e.preventDefault();
    setBusy(true); setErr(""); setMsg("");
    try {
      const { data } = await api.post("/strikes", form);
      setForm({ username: "", reason: "" });
      setMsg(data.auto_banned
        ? `Strike issued — user AUTO-BANNED at ${data.active_count} active strikes.`
        : `Strike issued (${data.active_count}/3 active).`);
      setTimeout(() => setMsg(""), 3000);
      await reload();
    } catch (e2) { setErr(fmtErr(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  }
  async function removeStrike(id) {
    if (!window.confirm("Remove this strike?")) return;
    try { await api.delete(`/strikes/${id}`); await reload(); }
    catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
  }

  return (
    <div className="bg-slate-900/40 border border-rose-500/30 rounded-lg p-3" data-testid="strikes-panel">
      <div className="text-[10px] uppercase font-bold text-rose-300 mb-2 flex items-center gap-1.5">
        <i className="fa-solid fa-gavel"/> Strikes
        <span className="text-slate-500 normal-case text-[9px] ml-auto">3 active = auto-ban · 72h auto-expiry</span>
      </div>
      <form onSubmit={issue} className="space-y-1.5 mb-2" data-testid="strike-form">
        <input required placeholder="Username or email" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })}
          data-testid="strike-username-input"
          className="w-full px-2 py-1 bg-[#161d28] border border-slate-700 rounded text-xs"/>
        <input required placeholder="Reason (required, 1-300 chars)" value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })}
          maxLength={300}
          className="w-full px-2 py-1 bg-[#161d28] border border-slate-700 rounded text-xs"/>
        <button type="submit" disabled={busy} data-testid="strike-issue"
          className="w-full py-1.5 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white rounded text-xs font-bold">
          {busy ? "Issuing…" : "Issue Strike"}
        </button>
      </form>
      {msg && <div className="text-[10px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-1 mb-1.5">{msg}</div>}
      {err && <div className="text-[10px] text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1 mb-1.5">{err}</div>}
      <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Recent strikes ({strikes.length})</div>
      <div className="max-h-40 overflow-y-auto space-y-1">
        {strikes.length === 0 && <div className="text-[10px] text-slate-600">No strikes on file.</div>}
        {strikes.map(s => (
          <div key={s.id} data-testid={`strike-${s.id}`}
            className={`bg-slate-900/40 border rounded px-2 py-1.5 text-[10px] ${s.active ? "border-rose-500/30" : "border-slate-800 opacity-60"}`}>
            <div className="flex items-center justify-between">
              <span className={`font-bold ${s.active ? "text-rose-300" : "text-slate-500"}`}>
                {s.active ? "ACTIVE" : (s.removed_at ? "REMOVED" : "EXPIRED")}
              </span>
              <span className="text-slate-500">{(s.issued_at || "").slice(0,16).replace("T"," ")}</span>
            </div>
            <div className="text-white mt-0.5">{s.user_name || s.user_email} · <span className="italic text-slate-400">"{s.reason}"</span></div>
            {s.active && (
              <button data-testid={`strike-remove-${s.id}`} onClick={() => removeStrike(s.id)}
                className="mt-1 text-[10px] text-rose-300 hover:text-rose-200">Remove</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
